// src/shared/utils/dateFormat.js

/** Pad a number to 2 digits: 3 → "03" */
export function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Parse "YYYY-MM-DD" string to a Date (local timezone). Returns null if invalid. */
export function ymdToDate(ymd) {
  if (!ymd) return null;
  const match = String(ymd).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const date = new Date(y, m - 1, d);

  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }

  return date;
}

/** Format a Date as "YYYY-MM-DD". Returns "" if invalid. */
export function fmtYMD(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Format a Date as "DD/MM". Returns "--/--" if invalid. */
export function fmtDM(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "--/--";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
}

/** Format a Date as "DD/MM/YYYY". Returns "" if invalid. */
export function fmtDMY(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Format a "YYYY-MM-DD" string as "DD/MM/YYYY". */
export function fmtDMYFromYMD(ymd) {
  const d = ymdToDate(ymd);
  return d ? fmtDMY(d) : "";
}

/**
 * Formatea un período sin repetir el año cuando ambas fechas pertenecen
 * al mismo año. Ejemplos:
 * - 2026-06-08 / 2026-06-15 → "08/06 - 15/06"
 * - 2025-12-29 / 2026-01-05 → "29/12/2025 - 05/01/2026"
 */
export function fmtCompactDateRange(start, end, separator = " - ") {
  const startDate = ymdToDate(String(start || "").slice(0, 10));
  const endDate = ymdToDate(String(end || "").slice(0, 10));

  if (!startDate || !endDate) return "—";

  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const format = sameYear ? fmtDM : fmtDMY;

  return `${format(startDate)}${separator}${format(endDate)}`;
}

const localDateTimeFormatter = new Intl.DateTimeFormat("es-AR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "America/Argentina/Buenos_Aires",
});

/** Format a Date or ISO datetime as "DD/MM/YYYY HH:mm" in Argentina. */
export function fmtDateTime(value, fallback = "—") {
  if (!value) return fallback;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;

  const parts = Object.fromEntries(
    localDateTimeFormatter
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
}

/** Add n days to a "YYYY-MM-DD" string. Returns "" if invalid. */
export function addDaysYMD(ymd, n) {
  const d = ymdToDate(ymd);
  if (!d) return "";
  d.setDate(d.getDate() + n);
  return fmtYMD(d);
}

/** Add n months to a "YYYY-MM-DD" string, clamping to last day of month. Returns "" if invalid. */
export function addMonthsYMD(ymd, n) {
  const d = ymdToDate(ymd);
  if (!d) return "";
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return fmtYMD(d);
}

/** Format a time string "HH:MM:SS" or "HH:MM" to "HH:MM". Returns "" if falsy. */
export function fmtTimeHHMM(t) {
  if (!t) return "";
  return String(t).slice(0, 5);
}

/** Validate a time string "HH:MM". Empty string is considered valid (optional field). */
export function isValidTimeHHMM(s) {
  if (!s) return true;
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
}

/**
 * Convierte cualquier valor (string "YYYY-MM-DD", datetime ISO, Date) al formato
 * "YYYY-MM-DD" que espera un <input type="date">. Devuelve "" si inválido.
 */
export function toISODate(value) {
  if (!value) return "";
  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match && ymdToDate(match[1]) ? match[1] : "";
  }
  if (value instanceof Date) return fmtYMD(value);
  return "";
}

/**
 * Parsea cualquier formato de fecha común ("DD/MM/YYYY", "DD/MM", "YYYY-MM-DD",
 * datetime ISO "YYYY-MM-DDTHH:mm:ss", o Date) y devuelve "YYYY-MM-DD".
 * Devuelve "" si no puede parsear.
 *
 * Si el formato es "DD/MM" (sin año), conserva el `referenceYearOrDate` recibido
 * (ej: el año de la fecha original o del período) o, si no existe, el año actual.
 */
export function parseDateAny(value, referenceYearOrDate = null) {
  if (!value) return "";

  if (value instanceof Date && !Number.isNaN(value.getTime())) return fmtYMD(value);

  const s = String(value).trim();

  // YYYY-MM-DD
  const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return ymdToDate(isoMatch[1]) ? isoMatch[1] : "";

  // DD/MM/YYYY o DD/MM/YY (con separador / - .)
  const dmyMatch = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (dmyMatch) {
    let y = Number(dmyMatch[3]);
    if (y < 100) y = 2000 + y;
    const mm = String(dmyMatch[2]).padStart(2, "0");
    const dd = String(dmyMatch[1]).padStart(2, "0");
    const candidate = `${y}-${mm}-${dd}`;
    if (ymdToDate(candidate)) return candidate;
  }

  // DD/MM (sin año, con separador / - .)
  const dmMatch = s.match(/^(\d{1,2})[/.-](\d{1,2})$/);
  if (dmMatch) {
    const dd = String(dmMatch[1]).padStart(2, "0");
    const mm = String(dmMatch[2]).padStart(2, "0");
    if (Number(mm) >= 1 && Number(mm) <= 12 && Number(dd) >= 1 && Number(dd) <= 31) {
      let year;
      if (typeof referenceYearOrDate === "number" && referenceYearOrDate > 1900) {
        year = referenceYearOrDate;
      } else if (typeof referenceYearOrDate === "string") {
        const refStr = referenceYearOrDate.trim();
        const yearMatch = refStr.match(/^(\d{4})/);
        if (yearMatch) {
          year = Number(yearMatch[1]);
        } else {
          const dtRef = new Date(refStr);
          year = !Number.isNaN(dtRef.getTime()) ? dtRef.getFullYear() : new Date().getFullYear();
        }
      } else if (referenceYearOrDate instanceof Date && !Number.isNaN(referenceYearOrDate.getTime())) {
        year = referenceYearOrDate.getFullYear();
      } else {
        year = new Date().getFullYear();
      }
      const candidate = `${year}-${mm}-${dd}`;
      return ymdToDate(candidate) ? candidate : "";
    }
  }

  // Solo intentar fallback Date() si no contiene separadores de fecha que hayan fallado
  if (!s.includes("/") && !s.includes("-") && !s.includes(".")) {
    const dt = new Date(value);
    if (!Number.isNaN(dt.getTime())) return fmtYMD(dt);
  }

  return "";
}
/**
 * Convierte un string "YYYY-MM-DD" a un ISO string con el offset de timezone
 * local (ej: "2004-06-17T00:00:00.000-03:00").
 *
 * Uso: mandar fechas al backend para que interprete el día en hora local,
 * evitando que "2004-06-17" se convierta a "2004-06-16T21:00:00Z" (UTC-3).
 *
 * @param {string} ymd  "YYYY-MM-DD"
 * @returns {string}  ISO string con timezone local, o "" si inválido
 */
export function toLocalISOString(ymd) {
  if (!ymd) return "";
  const d = ymdToDate(ymd);
  if (!d) return "";
  const offsetMs = d.getTimezoneOffset() * 60 * 1000;
  const localMs = d.getTime() - offsetMs;
  return new Date(localMs).toISOString().replace("Z", getOffsetSuffix(d));
}

/** Devuelve el sufijo de offset local tipo "-03:00" o "+05:30". */
function getOffsetSuffix(d) {
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? "+" : "-";
  const abs = Math.abs(off);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `${sign}${hh}:${mm}`;
}
